import { useRef, memo, useState, useCallback, useEffect } from 'react';
import Select from 'react-select';
import Pagination from '../common/pagination';
import FilterModal from './filter';
import { useAdmissionYear } from '../../hooks/studentlist/useAdmissionYear';
import { useStudentList } from '../../hooks/studentlist/useStudentList';
import { useSubmitAll, ACTION_STATUSES } from '../../hooks/studentlist/useSubmitAll';
import { useDownload } from '../../hooks/studentlist/useDownload';
import { useStatistics } from '../../hooks/studentlist/useStatistics';

const STATUS_CONFIG = {
    entered: { label: 'Pending', badgeClass: 'bg-warning-subtle text-warning border border-warning-subtle', icon: 'bi-clock-fill', selectable: true },
    is_forwarded: { label: 'IS Forwarded', badgeClass: 'bg-success-subtle text-success border border-success-subtle', icon: 'bi-check-circle-fill', selectable: false },
    is_reverted: { label: 'IS Reverted', badgeClass: 'bg-danger-subtle text-danger border border-danger-subtle', icon: 'bi-x-circle-fill', selectable: false },
    principal_forwarded: { label: 'Principal Forwarded', badgeClass: 'bg-success-subtle text-success border border-success-subtle', icon: 'bi-check-circle-fill', selectable: false },
    principal_reverted: { label: 'Principal Reverted', badgeClass: 'bg-danger-subtle text-danger border border-danger-subtle', icon: 'bi-x-circle-fill', selectable: false },
    ino_forwarded: { label: 'INO Forwarded', badgeClass: 'bg-success-subtle text-success border border-success-subtle', icon: 'bi-check-circle-fill', selectable: false },
    ino_reverted: { label: 'INO Reverted', badgeClass: 'bg-danger-subtle text-danger border border-danger-subtle', icon: 'bi-x-circle-fill', selectable: false },
    na: { label: 'N/A', badgeClass: 'bg-secondary-subtle text-secondary border border-secondary-subtle', icon: 'bi-dash-circle', selectable: false },
};

const getEffectiveKey = (item, optimisticStatuses) =>
    optimisticStatuses[item.imc_id] || item.entry_status;

const getEffectiveConfig = (item, optimisticStatuses) =>
    STATUS_CONFIG[getEffectiveKey(item, optimisticStatuses)] ?? STATUS_CONFIG['na'];

function AdmissionYearSelector({ value, options, loading, selectedOption, onChange }) {
    return (
        <div style={{ position: 'relative', width: '100%', paddingTop: '6px' }}>
            <span style={{
                position: 'absolute',
                top: '0px',
                left: '10px',
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#581f37',
                backgroundColor: '#f8f9fa',
                padding: '0 5px',
                zIndex: 2,
                letterSpacing: '0.04em',
                lineHeight: 1,
                userSelect: 'none',
            }}>
                ADMISSION YEAR <span style={{ color: '#dc3545' }}>*</span>
            </span>

            <Select
                options={options}
                value={selectedOption}
                onChange={opt => onChange(opt ? String(opt.value) : '')}
                isLoading={loading}
                isClearable
                placeholder={loading ? 'Loading years...' : 'Select admission year…'}
                noOptionsMessage={() => 'No years found'}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                    control: (base, state) => ({
                        ...base,
                        width: '100%',
                        borderColor: !value && !loading ? '#dc3545' : state.isFocused ? '#581f37' : '#ced4da',
                        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(88,31,55,.2)' : 'none',
                        borderRadius: '6px',
                        minHeight: '40px',
                        '&:hover': { borderColor: '#581f37' },
                        backgroundColor: 'white',
                    }),
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    placeholder: base => ({ ...base, color: '#adb5bd', fontSize: '0.9rem' }),
                    singleValue: base => ({ ...base, fontSize: '0.9rem', fontWeight: 500 }),
                }}
            />

            {!value && !loading && (
                <div style={{ color: '#dc3545', fontSize: '0.75rem', marginTop: '4px', paddingLeft: '2px' }}>
                    Please select an admission year to view the student list.
                </div>
            )}
        </div>
    );
}

function StudentList({ onViewMarks, refreshKey, enabled = true }) {
    const filterModalRef = useRef();
    const [selectedStudents, setSelectedStudents] = useState([]);

    const {
        admissionYear,
        options: yearOptions,
        loading: yearLoading,
        selectedOption: yearSelectedOption,
        handleAdmissionYearChange,
    } = useAdmissionYear();

    const {
        students,
        loading,
        currentPage,
        lastPage,
        total,
        perPage,
        appliedFilters,
        hasActiveFilters,
        handlePageChange,
        handleApplyFilter,
        handleClearFilters,
    } = useStudentList(refreshKey, enabled, admissionYear);

    const { isDownloading, handleDownload } = useDownload(appliedFilters, admissionYear);

    const {
        isApproving,
        forwardedImcIds,
        optimisticStatuses,
        selectableSelectedCount,
        handleSubmit,
        handleApprove,
    } = useSubmitAll({ students, selectedStudents, setSelectedStudents, onActionComplete: null, onSubmitSuccess: () => fetchStatusCounts(appliedFilters, admissionYear) });

    useEffect(() => {
        const lockedImcIds = students
            .filter(s => {
                const key = getEffectiveKey(s, optimisticStatuses);
                return !ACTION_STATUSES.includes(key) && key !== 'na';
            })
            .map(s => s.imc_id);

        setSelectedStudents(prev => [...new Set([...lockedImcIds, ...prev])]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [students]);

    const handleCheckboxChange = useCallback((imcId, effectiveKey) => {
        if (!ACTION_STATUSES.includes(effectiveKey)) return;
        setSelectedStudents(prev =>
            prev.includes(imcId) ? prev.filter(id => id !== imcId) : [...prev, imcId]
        );
    }, []);

    const handleSelectAll = useCallback((e) => {
        const permanentImcIds = students
            .filter(s => {
                const key = getEffectiveKey(s, optimisticStatuses);
                return (!ACTION_STATUSES.includes(key) || forwardedImcIds.includes(s.imc_id)) && key !== 'na';
            })
            .map(s => s.imc_id);

        if (e.target.checked) {
            const selectableImcIds = students
                .filter(s => {
                    const key = getEffectiveKey(s, optimisticStatuses);
                    return ACTION_STATUSES.includes(key) && !forwardedImcIds.includes(s.imc_id);
                })
                .map(s => s.imc_id);
            setSelectedStudents([...new Set([...permanentImcIds, ...selectableImcIds])]);
        } else {
            setSelectedStudents([...new Set(permanentImcIds)]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [students, forwardedImcIds, optimisticStatuses]);

    const handleClearSelection = useCallback(() => {
        const lockedImcIds = students
            .filter(s => {
                const key = getEffectiveKey(s, optimisticStatuses);
                return !ACTION_STATUSES.includes(key) && key !== 'na';
            })
            .map(s => s.imc_id);
        setSelectedStudents(lockedImcIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [students, optimisticStatuses]);

    const selectableStudents = students.filter(s => {
        const key = getEffectiveKey(s, optimisticStatuses);
        return ACTION_STATUSES.includes(key) && !forwardedImcIds.includes(s.imc_id);
    });

    const isAllSelected = selectableStudents.length > 0 &&
        selectableStudents.every(s => selectedStudents.includes(s.imc_id));

    const isSomeSelected = selectableStudents.some(s => selectedStudents.includes(s.imc_id)) &&
        !isAllSelected;

    const { statusCounts, statusLoading, fetchStatusCounts } = useStatistics();

    useEffect(() => {
        fetchStatusCounts(appliedFilters, admissionYear);
    }, [appliedFilters, fetchStatusCounts, refreshKey, admissionYear]);

    const handleStatusBadgeClick = (statusKey) => {
        const statusMap = {
            submitted: 'Submitted',
            draft: 'Draft',
            pending: 'Pending'
        };

        const currentStatus = appliedFilters?.status;
        const clickedStatus = statusMap[statusKey];

        if (currentStatus === clickedStatus) {
            handleApplyFilter({ ...appliedFilters, status: '' });
        } else {
            handleApplyFilter({ ...appliedFilters, status: clickedStatus });
        }
    };

    return (
        <>
            <div className="card">
                <div className="col-md-12">

                    <div className="card-header" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="py-3">

                            <div className="d-flex justify-content-between align-items-center">
                                <h5 style={{ color: '#581f37', fontWeight: 700, margin: 0 }}>
                                    Student Topic List
                                </h5>

                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                    {[
                                        { key: 'selected', label: 'Selected', bg: '#1f347a', status: 'Selected' },
                                        { key: 'forwarded', label: 'Forwarded', bg: '#1f7a45', status: 'Forwarded' },
                                        { key: 'pending', label: 'Pending', bg: '#b7791f', status: 'Pending' },
                                    ].map(s => {
                                        const isActive = appliedFilters?.status === s.status;
                                        return (
                                            <div
                                                key={s.key}
                                                onClick={() => handleStatusBadgeClick(s.key)}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                    padding: '3px 8px 3px 5px',
                                                    borderRadius: '999px',
                                                    background: s.bg,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    userSelect: 'none',
                                                    boxShadow: isActive ? `0 0 0 2px ${s.bg}44` : 'none',
                                                }}
                                            >
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minWidth: '16px',
                                                    height: '16px',
                                                    borderRadius: '999px',
                                                    background: 'rgba(255,255,255,0.25)',
                                                    color: '#ffffff',
                                                    fontSize: '10px',
                                                    fontWeight: 700,
                                                    padding: '0 3px',
                                                    lineHeight: 1,
                                                }}>
                                                    {statusLoading ? '…' : (statusCounts?.[s.key] ?? 0)}
                                                </span>

                                                <span style={{
                                                    fontSize: '10px',
                                                    fontWeight: 500,
                                                    color: '#ffffff',
                                                    lineHeight: 1,
                                                }}>
                                                    {s.label}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="mt-2">
                                <AdmissionYearSelector
                                    value={admissionYear}
                                    options={yearOptions}
                                    loading={yearLoading}
                                    selectedOption={yearSelectedOption}
                                    onChange={handleAdmissionYearChange}
                                />
                            </div>

                        </div>
                    </div>

                    {admissionYear ? (
                        <div className="card-body">

                            <div className="row align-items-center mt-0 mb-1">
                                <div className="col-12 d-flex flex-wrap align-items-center gap-2 justify-content-between">

                                    <div className="d-flex flex-wrap gap-2">
                                        <span title={selectableSelectedCount === 0 ? 'Select at least one student to enable this button' : 'Approve selected students'}>
                                            <button
                                                type="button"
                                                className="btn btn-primary d-flex align-items-center gap-2"
                                                disabled={selectableSelectedCount === 0 || isApproving}
                                                style={selectableSelectedCount === 0 ? { pointerEvents: 'none' } : undefined}
                                                onClick={handleApprove}
                                            >
                                                {isApproving ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                        Forwarding...
                                                    </>
                                                ) : (
                                                    <>Forward {selectableSelectedCount > 0 && `(${selectableSelectedCount})`}</>
                                                )}
                                            </button>
                                        </span>

                                        {selectableSelectedCount > 0 && !isApproving && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                title="Clear selection"
                                                onClick={handleClearSelection}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                        <style>{`
                                            .dot-loader { display: inline-flex; gap: 4px; }
                                            .dot-loader span { width: 6px; height: 6px; background: white; border-radius: 50%; display: inline-block; animation: dotPulse 1.4s infinite ease-in-out; }
                                            .dot-loader span:nth-child(2) { animation-delay: 0.2s; }
                                            .dot-loader span:nth-child(3) { animation-delay: 0.4s; }
                                            @keyframes dotPulse { 0%, 80%, 100% { opacity: .3; transform: scale(.7); } 40% { opacity: 1; transform: scale(1); } }
                                        `}</style>

                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary text-light d-flex align-items-center gap-2"
                                            onClick={handleDownload}
                                            disabled={isDownloading}
                                        >
                                            {isDownloading ? (
                                                <>Downloading <span className="dot-loader"><span /><span /><span /></span></>
                                            ) : (
                                                <>Download <i className="fa fa-download ms-1" /></>
                                            )}
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-primary position-relative"
                                            title="Filter"
                                            onClick={() => filterModalRef.current?.open()}
                                        >
                                            Filter <i className="fa fa-filter ms-2" />
                                            {hasActiveFilters && (
                                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                                    <i className="fa fa-info" style={{ fontSize: '10px' }} />
                                                    <span className="visually-hidden">filters applied</span>
                                                </span>
                                            )}
                                        </button>

                                        {hasActiveFilters && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                title="Clear filters"
                                                onClick={handleClearFilters}
                                            >
                                                <i className="fa fa-times" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <div className="table-container mb-0" style={{ overflowX: 'unset' }}>
                                    <table className="display dataTable table-striped table-bordered ex-v-table">
                                        <thead>
                                            <tr className="bg-primary">
                                                <th width="5%" className="text-center">
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            className="round-checkbox"
                                                            checked={isAllSelected}
                                                            ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                                                            onChange={handleSelectAll}
                                                            disabled={loading || selectableStudents.length === 0}
                                                            title={selectableStudents.length === 0 ? 'No selectable entries' : 'Select all'}
                                                        />
                                                        <span>No</span>
                                                    </div>
                                                </th>
                                                <th width="16%">Student</th>
                                                <th className="text-center" width="15%">IPO</th>
                                                <th className="text-center" width="15%">Topic</th>
                                                <th className="text-center" width="15%">Discipline</th>
                                                <th className="text-center" width="15%">Course</th>
                                                <th className="text-center" width="13%">Status</th>
                                                <th className="text-center" width="14%">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-4">
                                                        <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: '#581f37' }}>
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                        Loading students...
                                                    </td>
                                                </tr>
                                            ) : students.length ? (
                                                students.map((item, index) => {
                                                    const effectiveKey = getEffectiveKey(item, optimisticStatuses);
                                                    const statusCfg = getEffectiveConfig(item, optimisticStatuses);
                                                    const isActionable = ACTION_STATUSES.includes(effectiveKey) && !forwardedImcIds.includes(item.imc_id);
                                                    const isLocked = !isActionable;
                                                    const isChecked = selectedStudents.includes(item.imc_id);

                                                    return (
                                                        <tr
                                                            key={`${item.id}-${index}`}
                                                            className={isLocked ? 'table-secondary' : ''}
                                                            style={{
                                                                opacity: isLocked ? 1 : 1,
                                                                backgroundColor: isLocked ? '#f8f9fa' : 'inherit',
                                                            }}
                                                        >
                                                            <td data-label="No" className="text-center" width="5%">
                                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        className={`round-checkbox${effectiveKey === 'is_reverted' || effectiveKey === 'principal_reverted' || effectiveKey === 'ino_reverted' ? ' round-checkbox--danger' : ''}`}
                                                                        value={item.imc_id}
                                                                        checked={isChecked}
                                                                        onChange={() => handleCheckboxChange(item.imc_id, effectiveKey)}
                                                                        disabled={isLocked}
                                                                        title={isLocked ? `${statusCfg.label} — cannot select` : 'Select student'}
                                                                        style={{ border: "1px solid black" }}
                                                                    />
                                                                    <span>{(currentPage - 1) * perPage + index + 1}</span>
                                                                </div>
                                                            </td>

                                                            <td data-label="Student">{item.student || '-'}</td>
                                                            <td data-label="IPO" className="text-center">{item.ipo || '-'}</td>
                                                            <td data-label="Topic" className="text-center">{item.topic || '-'}</td>
                                                            <td data-label="Discipline" className="text-center">{item.discipline || '-'}</td>
                                                            <td data-label="Course" className="text-center">{item.course || '-'}</td>

                                                            <td data-label="Status" className="text-center">
                                                                <span
                                                                    className={`badge rounded-pill px-2 py-1 d-inline-flex align-items-center gap-1 ${statusCfg.badgeClass}`}
                                                                    style={{ fontSize: '0.72rem', fontWeight: 500 }}
                                                                >
                                                                    <i className={`bi ${statusCfg.icon}`} style={{ fontSize: '0.7rem' }} />
                                                                    {statusCfg.label}
                                                                </span>
                                                            </td>

                                                            <td data-label="Action" className="text-center">
                                                                {isActionable ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger btn-sm d-inline-flex align-items-center gap-1"
                                                                        title="Revert this student"
                                                                        onClick={() => handleSubmit(item.imc_id, 'reject')}
                                                                    >
                                                                        Revert
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-4">
                                                        <i className="fa fa-inbox fa-2x text-muted mb-2" />
                                                        <p className="mb-0">No students found</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="row align-items-center mt-3">
                                <div className="col-md-6 col-12 d-flex justify-content-md-start gap-2">
                                    <p className="mb-0 text-muted">
                                        Showing {students.length > 0 ? (currentPage - 1) * perPage + 1 : 0} to {Math.min(currentPage * perPage, total)} of {total} entries
                                        {selectableSelectedCount > 0 && (
                                            <span className="badge bg-primary ms-2">{selectableSelectedCount} selected</span>
                                        )}
                                    </p>
                                </div>
                                <div className="col-md-6 col-12 d-flex justify-content-md-end justify-content-center">
                                    <Pagination
                                        currentPage={currentPage}
                                        lastPage={lastPage}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </div>

                        </div>
                    ) : (
                        <div className="card-body text-center py-5">
                            <i className="bi bi-calendar-x text-muted" style={{ fontSize: '3rem' }} />
                            <p className="text-muted mt-3 mb-0">Select an admission year above to view the student list.</p>
                        </div>
                    )}
                </div>
            </div>

            <FilterModal
                ref={filterModalRef}
                onApplyFilter={handleApplyFilter}
                initialFilters={appliedFilters}
            />

            <style>{`
                .round-checkbox {
                    appearance: none; -webkit-appearance: none; -moz-appearance: none;
                    width: 18px; height: 18px;
                    border: 2px solid #6c757d; border-radius: 4px;
                    outline: none; cursor: pointer; position: relative;
                    background-color: white; transition: all 0.2s ease; flex-shrink: 0;
                }
                .round-checkbox:hover:not(:disabled) { border-color: #28a745; box-shadow: 0 0 0 0.15rem rgba(40,167,69,.15); }
                .round-checkbox:checked { background-color: #28a745; border-color: #28a745; }
                .round-checkbox:checked::before {
                    content: '✓'; position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%); color: white; font-size: 14px; font-weight: bold; line-height: 1;
                }
                .round-checkbox:indeterminate { background-color: #28a745; border-color: #28a745; }
                .round-checkbox:indeterminate::before {
                    content: ''; position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%); width: 10px; height: 2px; background-color: white;
                }
                .round-checkbox:disabled { opacity: 0.5; cursor: not-allowed; border-color: #6c757d; background-color: #e9ecef; }
                .round-checkbox:disabled:checked { background-color: #28a745; border-color: #28a745; opacity: 0.7; }
                .round-checkbox:focus { border-color: #28a745; box-shadow: 0 0 0 0.25rem rgba(40,167,69,.25); }
                .form-check-input:checked { background-color: #581f37; border-color: #581f37; }
                .form-check-input:focus { border-color: #581f37; box-shadow: 0 0 0 0.25rem rgba(88,31,55,.25); }
                .table-secondary { background-color: #f8f9fa !important; }
                tr.table-secondary:hover { background-color: #e9ecef !important; }
                table.dataTable input, table.dataTable select { height: 20px !important; }
                .round-checkbox--danger:checked { background-color: #dc3545; border-color: #dc3545; }
                .round-checkbox--danger:checked::before {
                    content: '✓'; position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%); color: white; font-size: 14px; font-weight: bold; line-height: 1;
                }
                .round-checkbox--danger:disabled:checked { background-color: #dc3545; border-color: #dc3545; opacity: 0.7; }
                .round-checkbox--danger:hover:not(:disabled) { border-color: #dc3545; box-shadow: 0 0 0 0.15rem rgba(220,53,69,.15); }
                .round-checkbox--danger:focus { border-color: #dc3545; box-shadow: 0 0 0 0.25rem rgba(220,53,69,.25); }
            `}</style>
        </>
    );
}

export default memo(StudentList);